"use client"

import { Text } from "@/elements/Text"
import { ChevronRightIcon } from "@/icons/outline"
import { ArrowLeftIcon } from "@heroicons/react/24/outline"
import clsx from "clsx"
import Link from "next/link"
import { useRouter, useSearchParams } from "next/navigation"
import React from "react"
import { useNavigationStore } from "@/providers/NavigationProvider/navigationStore"

export type Crumbs = { text: string; link?: string }[]

interface Props {
  className?: string
  breadCrumbs?: Crumbs
}

export const BreadCrumbs: React.FC<Props> = ({ className, breadCrumbs }) => {
  const searchParams = useSearchParams()

  const refLinks = searchParams?.get("refLinks")?.split(",") || []
  const refTitles = searchParams?.get("refTitles")?.split(",") || []

  const crumbs = breadCrumbs?.length ? breadCrumbs : refLinks.map((link, index) => ({ text: refTitles[index], link }))

  if (crumbs.length === 0) {
    return <div />
  }

  return (
    <nav className={clsx("flex flex-row items-center gap-x-2", className)}>
      {crumbs.length > 1 ? (
        crumbs.map((crumb, index) => (
          <div key={index} className="flex flex-row items-center">
            {index !== 0 && (
              <div className="mr-2">
                <ChevronRightIcon className="size-[18px] stroke-fg-3" />
              </div>
            )}
            {crumb.link ? (
              <LinkOrBack href={crumb.link} className="flex w-fit flex-row items-center gap-x-1">
                <Text className="hidden text-fg-2 md:block" variant="body1">
                  {crumb.text}
                </Text>
              </LinkOrBack>
            ) : (
              <Text className="hidden text-fg-2 md:block" variant="body1">
                {crumb.text}
              </Text>
            )}
          </div>
        ))
      ) : (
        <LinkOrBack href={crumbs[0].link || ""} className={clsx("flex w-fit flex-row items-center gap-x-2")}>
          {crumbs[0].link && <ArrowLeftIcon height={22} width={22} />}
          <Text className="hidden md:block" variant="CTASmall">
            {crumbs[0].text}
          </Text>
        </LinkOrBack>
      )}
    </nav>
  )
}

export const LinkOrBack = ({
  href,
  className,
  children,
}: {
  href?: string
  className?: string
  children: React.ReactNode
}) => {
  const { getPreviousPath, back } = useNavigationStore()
  const router = useRouter()
  const previousPath = getPreviousPath()
  if (previousPath === href || !href) {
    return (
      <div
        onClick={() => {
          router.back()
          back()
        }}
        className={clsx("cursor-pointer", className)}>
        {children}
      </div>
    )
  } else {
    return (
      <Link href={href} className={className}>
        {children}
      </Link>
    )
  }
}
